// A sparse 64-second original score. Notes have soft attacks and natural decay;
// rests and changing harmony replace the previous continuous oscillator drone.
export const AMBIENT_LOOP_SECONDS = 64;
export const AMBIENT_NOTES = [
  [0, 60, .75], [2.5, 67, .48], [5, 71, .40], [9, 64, .55], [12, 67, .35],
  [16, 57, .70], [18.5, 64, .45], [21.5, 69, .40], [25, 71, .32], [28, 64, .45],
  [32, 53, .72], [35, 60, .48], [38, 64, .42], [41, 69, .33], [44, 67, .38],
  [48, 55, .70], [51, 62, .45], [54, 69, .35], [57, 67, .40], [60, 62, .30],
] as const;

export function createAmbientBus(context: BaseAudioContext, destination: AudioNode) {
  const input = context.createBiquadFilter();
  input.type = 'lowpass';
  input.frequency.value = 1600;
  input.Q.value = .4;
  const echo = context.createDelay(1);
  echo.delayTime.value = .38;
  const wet = context.createGain();
  wet.gain.value = .18;
  const output = context.createGain();
  input.connect(output);
  input.connect(echo).connect(wet).connect(output);
  output.connect(destination);
  return { input, output, disconnect: () => { input.disconnect(); echo.disconnect(); wet.disconnect(); output.disconnect(); } };
}

export function playAmbientNote(context: BaseAudioContext, destination: AudioNode, when: number, midi: number, velocity: number, pan: number): () => void {
  const fundamental = context.createOscillator();
  const overtone = context.createOscillator();
  const body = context.createGain();
  const shimmer = context.createGain();
  const stereo = context.createStereoPanner();
  const frequency = 440 * 2 ** ((midi - 69) / 12);
  fundamental.frequency.value = frequency;
  overtone.frequency.value = frequency * 2;
  stereo.pan.value = pan;
  // A rounded felt-mallet envelope, with a quieter, faster-decaying overtone.
  for (const [gain, peak, decay] of [[body.gain, .16 * velocity, 3.8], [shimmer.gain, .018 * velocity, 1.2]] as const) {
    gain.setValueAtTime(0, when);
    gain.linearRampToValueAtTime(peak, when + .045);
    gain.exponentialRampToValueAtTime(.00001, when + decay);
    gain.linearRampToValueAtTime(0, when + 4);
  }
  fundamental.connect(body).connect(stereo);
  overtone.connect(shimmer).connect(stereo);
  stereo.connect(destination);
  const cleanup = () => { fundamental.disconnect(); overtone.disconnect(); body.disconnect(); shimmer.disconnect(); stereo.disconnect(); };
  fundamental.onended = cleanup;
  fundamental.start(when);
  overtone.start(when);
  fundamental.stop(when + 4.05);
  overtone.stop(when + 4.05);
  return () => {
    // The bus fades before cancellation, avoiding a click when music is muted.
    const stopAt = context.currentTime + .07;
    fundamental.stop(stopAt);
    overtone.stop(stopAt);
  };
}

export class AmbientMusic {
  private bus: ReturnType<typeof createAmbientBus> | null = null;
  private scheduler: ReturnType<typeof setInterval> | null = null;
  private voices: { end: number; stop: () => void }[] = [];
  constructor(private context: AudioContext) {}

  start(volume: number): void {
    if (this.bus) return;
    const ctx = this.context;
    const bus = createAmbientBus(ctx, ctx.destination);
    this.bus = bus;
    bus.output.gain.setValueAtTime(0, ctx.currentTime);
    bus.output.gain.linearRampToValueAtTime(volume * .35, ctx.currentTime + 1.2);
    let loopStart = ctx.currentTime + .12;
    let index = 0;
    const schedule = () => {
      this.voices = this.voices.filter(v => v.end > ctx.currentTime);
      while (loopStart + AMBIENT_NOTES[index]![0] < ctx.currentTime + .4) {
        const [offset, note, velocity] = AMBIENT_NOTES[index]!;
        const when = loopStart + offset;
        // Skip stale notes after a stalled tab; never play a burst to catch up.
        if (when >= ctx.currentTime) {
          const stop = playAmbientNote(ctx, bus.input, when, note, velocity, index % 2 ? .22 : -.22);
          this.voices.push({ end: when + 4.1, stop });
        }
        index++;
        if (index === AMBIENT_NOTES.length) { index = 0; loopStart += AMBIENT_LOOP_SECONDS; }
      }
    };
    schedule();
    this.scheduler = setInterval(schedule, 200);
  }

  setVolume(volume: number): void {
    const gain = this.bus?.output.gain;
    if (!gain) return;
    gain.cancelAndHoldAtTime(this.context.currentTime);
    gain.setTargetAtTime(volume * .35, this.context.currentTime, .1);
  }

  stop(): void {
    if (this.scheduler !== null) clearInterval(this.scheduler);
    this.scheduler = null;
    const bus = this.bus;
    this.bus = null;
    if (!bus) return;
    const now = this.context.currentTime;
    bus.output.gain.cancelAndHoldAtTime(now);
    bus.output.gain.linearRampToValueAtTime(0, now + .06);
    this.voices.forEach(v => v.stop());
    this.voices = [];
    setTimeout(bus.disconnect, 100);
  }
}
